/**
 * registry/EngineRegistry.ts — the roster of every engine Roamer Core™ manages.
 *
 * Each engine (built-in or future plugin) registers ONE adapter describing its
 * identity, version, capabilities, and lifecycle hooks. The registry supports
 * register / unregister / enable / disable / restart plus status introspection.
 * Future plugins simply call `register()` — no architectural change required.
 */

export type EngineStatus = 'registered' | 'running' | 'disabled' | 'failed' | 'recovering';

/** What every engine must expose to be managed by Roamer Core™. */
export interface EngineAdapter {
  id: string;
  name: string;
  version: string;
  capabilities: string[];
  /** Optional lifecycle hooks — engines without them are considered always-on. */
  start?(): Promise<void> | void;
  stop?(): Promise<void> | void;
  /** Health probe. Throw or return false = unhealthy. */
  healthCheck?(): Promise<boolean> | boolean;
}

export interface EngineEntry {
  adapter: EngineAdapter;
  status: EngineStatus;
  registeredAt: number;
  enabled: boolean;
}

export interface EngineInfo {
  id: string;
  name: string;
  version: string;
  capabilities: string[];
  status: EngineStatus;
  enabled: boolean;
  registeredAt: number;
}

export class EngineRegistry {
  private engines = new Map<string, EngineEntry>();

  register(adapter: EngineAdapter, now: number): void {
    this.engines.set(adapter.id, {
      adapter,
      status: 'running',
      registeredAt: now,
      enabled: true,
    });
  }

  unregister(id: string): boolean {
    return this.engines.delete(id);
  }

  enable(id: string): boolean {
    const entry = this.engines.get(id);
    if (!entry) return false;
    entry.enabled = true;
    entry.status = 'running';
    return true;
  }

  disable(id: string): boolean {
    const entry = this.engines.get(id);
    if (!entry) return false;
    entry.enabled = false;
    entry.status = 'disabled';
    return true;
  }

  async restart(id: string): Promise<boolean> {
    const entry = this.engines.get(id);
    if (!entry) return false;
    entry.status = 'recovering';
    try {
      await entry.adapter.stop?.();
      await entry.adapter.start?.();
      entry.status = 'running';
      return true;
    } catch {
      entry.status = 'failed';
      return false;
    }
  }

  setStatus(id: string, status: EngineStatus): void {
    const entry = this.engines.get(id);
    if (entry) entry.status = status;
  }

  get(id: string): EngineEntry | null {
    return this.engines.get(id) ?? null;
  }

  isEnabled(id: string): boolean {
    return this.engines.get(id)?.enabled ?? false;
  }

  list(): EngineInfo[] {
    return Array.from(this.engines.values()).map((e) => ({
      id: e.adapter.id,
      name: e.adapter.name,
      version: e.adapter.version,
      capabilities: [...e.adapter.capabilities],
      status: e.status,
      enabled: e.enabled,
      registeredAt: e.registeredAt,
    }));
  }
}
